import { useLazyQuery } from "@apollo/client";
import { compact } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  GetMeetingOverviewMeetingsQueryQuery,
  GetMeetingOverviewMeetingsQueryQueryVariables,
  MeetingDialogFragmentFragment,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { Select } from "@components/select/select";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import getMeetingOverviewMeetingsQuery from "../graphql/get-meeting-overview-meetings-query";
import { MeetingType } from "./meetings-overview-constants";
import OverviewMeetingList from "./overview-meeting-list";

const MeetingOverview = ({
  selectedDay,
  filterClassName,
  meetingListClassName,
  onClickMeetingLink,
  forceTodayScroll,
}: {
  forceTodayScroll: number;
  selectedDay: string;
  filterClassName?: string;
  meetingListClassName?: string;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
}) => {
  const selectedUser = currentUserVar();
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [initComplete, setInitComplete] = useState(false);
  const [loadMore, setLoadMore] = useState<"past" | "future" | null>(null);
  const [meetings, setMeetings] = useState<MeetingDialogFragmentFragment[]>([]);
  const [participantFilter, setParticipantFilter] = useState<number | null>(
    null,
  );
  const [meetingTypeFilter, setMeetingTypeFilter] = useState(MeetingType.ALL);

  const [meetingDisplayedRange, setMeetingDisplayedRange] = useState<{
    start: string;
    end: string;
  }>({
    start: moment(selectedDay).startOf("isoWeek").subtract(1, "week").format(),
    end: moment(selectedDay).startOf("isoWeek").add(2, "week").format(),
  });

  const [fetchMeetings, { loading }] = useLazyQuery<
    GetMeetingOverviewMeetingsQueryQuery,
    GetMeetingOverviewMeetingsQueryQueryVariables
  >(getMeetingOverviewMeetingsQuery, {});

  const handleReceiveMeetingsResponse = useCallback(
    (response: GetMeetingOverviewMeetingsQueryQuery) => {
      const meetings = compact(
        assertEdgesNonNull(response.calendar).map(
          (calendarItem) => calendarItem.meeting,
        ),
      );
      setMeetings((prev) => {
        const existingIds = prev.map(({ id }) => id);
        const updatedMeetings = prev.map((prevMeeting) => {
          if (meetings.map(({ id }) => id).includes(prevMeeting.id)) {
            return assertNonNull(
              meetings.find(({ id }) => id === prevMeeting.id),
            );
          }
          return prevMeeting;
        });
        const newMeetings = meetings.filter(
          ({ id }) => !existingIds.includes(id),
        );
        return [...updatedMeetings, ...newMeetings];
      });
      setInitComplete(true);
      setLoadMore(null);
    },
    [],
  );

  const scrollToToday = () => {
    if (todayRef.current && scrollContainerRef.current) {
      const scrollTop = todayRef.current.offsetTop;
      scrollContainerRef.current.scrollTo({
        top: scrollTop - 10,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (initComplete) {
      const scrollInterval = setTimeout(() => {
        scrollToToday();
      }, 700);
      return () => clearTimeout(scrollInterval);
    }
    // scroll to today only on page load
  }, [initComplete, selectedDay]);

  useEffect(() => {
    if (initComplete) {
      scrollToToday();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceTodayScroll]);

  useEffect(() => {
    if (loadMore === null || !initComplete) {
      return;
    }
    const dateRangeStart =
      loadMore === "past"
        ? moment(meetingDisplayedRange.start).subtract(1, "week").format()
        : meetingDisplayedRange.end;
    const dateRangeEnd = moment(dateRangeStart).add(1, "week").format();
    console.log(loadMore, {
      start: dateRangeStart,
      end: dateRangeEnd,
    });
    const sortedDates = [
      meetingDisplayedRange.start,
      meetingDisplayedRange.end,
      dateRangeStart,
      dateRangeEnd,
    ].sort();
    fetchMeetings({
      variables: {
        forUserId: selectedUser.id as number,
        dateRangeStart: dateRangeStart,
        dateRangeEnd: dateRangeEnd,
        orderBy: "start_datetime",
        first: null,
      },
      onCompleted: (response) => {
        handleReceiveMeetingsResponse(response);
        setMeetingDisplayedRange({
          start: sortedDates[0],
          end: sortedDates[3],
        });
      },
    });
    // only want to fetch more when index changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMore]);

  useEffect(() => {
    fetchMeetings({
      variables: {
        forUserId: selectedUser.id as number,
        dateRangeStart: meetingDisplayedRange.start,
        dateRangeEnd: meetingDisplayedRange.end,
        orderBy: "start_datetime",
        first: null,
      },
      onCompleted: (response) => {
        handleReceiveMeetingsResponse(response);
        setInitComplete(true);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    options: assessmentUserFilterOptions,
    query,
    setQuery,
  } = useUserComboboxQuery({});
  const userComboboxValue =
    assessmentUserFilterOptions.find(({ id }) => id === participantFilter) ||
    null;

  const handleChangeParticipantFilter = useCallback(
    (option?: UserComboboxOption) => {
      setParticipantFilter(
        option && typeof option.id === "number" ? option.id : null,
      );
    },
    [],
  );

  const handleMeetingUpdated = useCallback(
    (data: { meeting: MeetingDialogFragmentFragment }) => {
      if (data.meeting.startDatetime) {
        setMeetings((prev) => {
          if (prev.find((meeting) => meeting.id === data.meeting.id)) {
            return prev.map((meeting) => {
              if (meeting.id === data.meeting.id) {
                return data.meeting;
              }
              return meeting;
            });
          }
          return [...prev, data.meeting];
        });
      } else {
        // else remove from meetings list and update unscheduledMeetings
        setMeetings((prev) =>
          prev.filter((meeting) => meeting.id !== data.meeting.id),
        );
      }
    },
    [],
  );

  return (
    <div className="@container/overview-meetings h-full">
      <div className={classNames("sticky z-40 bg-white")}>
        <div
          className={classNames(
            "flex items-center justify-between gap-2 h-14 px-4 border-b",
            filterClassName,
          )}
        >
          <div className="w-1/2">
            <UserCombobox
              onChangeValue={handleChangeParticipantFilter}
              options={assessmentUserFilterOptions}
              value={userComboboxValue}
              placeholder="All people"
              clearable={participantFilter !== null}
              query={query}
              onChangeQuery={setQuery}
              onClearValue={handleChangeParticipantFilter}
              buttonProps={{ size: ButtonSize.medium }}
            />
          </div>
          <div className="w-1/2 flex">
            <div className="flex-1 w-full">
              <Select
                value={meetingTypeFilter}
                options={[
                  {
                    value: MeetingType.ALL,
                    label: "All meetings",
                  },
                  {
                    value: MeetingType.ONE_ON_ONE,
                    label: "1-on-1s",
                  },
                  {
                    value: MeetingType.GROUP,
                    label: "Group meetings",
                  },
                ]}
                onChange={(opt) => setMeetingTypeFilter(opt.value)}
                aria-label="Meeting type filter"
                buttonProps={{ size: ButtonSize.medium }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={classNames(
          "absolute inset-0 overflow-y-scroll px-6 pt-4 pb-12",
          "top-14",
          meetingListClassName,
        )}
        ref={scrollContainerRef}
      >
        {/* Main loader on first page hit */}
        {loading && !initComplete && (
          <Loading className="p-6" size={6}>
            Loading meetings...
          </Loading>
        )}

        {initComplete && (
          <OverviewMeetingList
            ref={todayRef}
            meetings={meetings}
            loading={loading}
            participantFilter={participantFilter}
            meetingTypeFilter={meetingTypeFilter}
            meetingDisplayedRange={meetingDisplayedRange}
            onMeetingUpdated={handleMeetingUpdated}
            onClickMeetingLink={onClickMeetingLink}
            loadMore={loadMore}
            onChangeLoadMore={setLoadMore}
            isShowingWeekends={true}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingOverview;
